/**
 * Janelas (modal central, gaveta lateral ou larga). Uma por vez, como nas prévias.
 *
 *   const dialog = useDialog();
 *   dialog.open(<ClientForm />, { drawer: true });
 *   // dentro do componente aberto: const { close } = useDialog();
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from './icons';

export interface DialogOptions {
  /** gaveta à direita (formulários longos) */
  drawer?: boolean;
  /** largura maior (tabelas, prévias) */
  wide?: boolean;
  className?: string;
  /** chamado ao fechar (por botão, Esc ou clique fora) */
  onClose?: () => void;
}

interface DialogApi {
  open: (content: ReactNode, opts?: DialogOptions) => void;
  close: () => void;
}

const DialogCtx = createContext<DialogApi | null>(null);

export function DialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ content: ReactNode; opts: DialogOptions; key: number } | null>(null);
  const [shown, setShown] = useState(false);
  const optsRef = useRef<DialogOptions>({});
  const lastFocus = useRef<HTMLElement | null>(null);

  const openRef = useRef(false);

  const close = useCallback(() => {
    if (!openRef.current) return; // nada aberto: não faz nada
    openRef.current = false;
    const onClose = optsRef.current.onClose;
    optsRef.current = {};
    setShown(false);
    onClose?.();
    window.setTimeout(() => setState(s => (openRef.current ? s : null)), 200);
    lastFocus.current?.focus?.();
  }, []);

  const open = useCallback((content: ReactNode, opts: DialogOptions = {}) => {
    lastFocus.current = document.activeElement as HTMLElement;
    optsRef.current = opts;
    openRef.current = true;
    setState({ content, opts, key: Date.now() });
    requestAnimationFrame(() => setShown(true));
  }, []);

  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [state, close]);

  const api = useMemo(() => ({ open, close }), [open, close]);
  const o = state?.opts ?? {};
  return (
    <DialogCtx.Provider value={api}>
      {children}
      {state && (
        <div
          className={`overlay${shown ? ' show' : ''}${o.drawer ? ' as-drawer' : ''}${o.wide ? ' wide' : ''} ${o.className ?? ''}`}
          onMouseDown={e => e.target === e.currentTarget && close()}
        >
          <div className="sheet" role="dialog" aria-modal="true" key={state.key}>
            {state.content}
          </div>
        </div>
      )}
    </DialogCtx.Provider>
  );
}

export function useDialog(): DialogApi {
  const ctx = useContext(DialogCtx);
  if (!ctx) throw new Error('useDialog fora do DialogProvider');
  return ctx;
}

/** Cabeçalho padrão das janelas, com botão de fechar. */
export function SheetHead({ title, sub, icon }: { title: ReactNode; sub?: ReactNode; icon?: ReactNode }) {
  const { close } = useDialog();
  return (
    <header className="sheet-head">
      <div>
        <h3>
          {icon}
          {title}
        </h3>
        {sub && <p>{sub}</p>}
      </div>
      <button type="button" className="icon-btn" onClick={close} aria-label="Fechar">
        <Icon name="x" />
      </button>
    </header>
  );
}

/** Rodapé com ação à esquerda (ex.: excluir) e ações principais à direita. */
export function SheetFoot({ left, children }: { left?: ReactNode; children: ReactNode }) {
  return (
    <footer className="sheet-foot">
      {left ?? <span />}
      <div>{children}</div>
    </footer>
  );
}

/** Pergunta de confirmação simples. */
export function Confirm({
  title,
  sub,
  confirmLabel,
  danger,
  onConfirm,
}: {
  title: string;
  sub?: string;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => void | Promise<void>;
}) {
  const { close } = useDialog();
  const [busy, setBusy] = useState(false);
  return (
    <>
      <SheetHead title={title} sub={sub} />
      <div className="sheet-body">
        <SheetFoot>
          <button className="btn ghost" onClick={close}>
            Cancelar
          </button>
          <button
            className={`btn ${danger ? 'danger' : 'primary'}`}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
                close();
              } catch {
                // o erro já foi avisado por quem chamou (ex.: useAction mostra o aviso)
              } finally {
                setBusy(false);
              }
            }}
          >
            {confirmLabel}
          </button>
        </SheetFoot>
      </div>
    </>
  );
}
